import React, {useEffect, useState} from 'react';
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/PageHeader";
import {FunnelIcon, QueueListIcon} from "@heroicons/react/24/outline";
import IconButton from "../../../components/IconButton";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import ButtonInternalLink from "../../../components/ButtonInternalLink";
import SelectInput from "../../../components/SelectInput";
import Loading from "../../../components/Loading";
import {apollo} from "../../../utils/apollo";
import {GET_MERCHANT_SUPPLIERS_LIST, GET_PAYMENT_BROKER_REFERENCES, GET_PAYMENT_CHANNELS, GET_PAYMENT_LIST, GET_PAYMENT_STATUS_COUNT} from "../../../utils/graphql";
import PaymentLinkDialog from "../../../components/PaymentLinkDialog";
import PaymentReportGenerateDialog from "../../../components/PaymentReportGenerateDialog";
import Button from "../../../components/Button";
import Tabs from "../../../components/Tabs";
import {PAYMENT_LIST_STATUS_FOR_FILTER} from "../../../utils/constants";
import CompactPaymentList from "./components/CompactPaymentList";
import usePreferenceStore from "../../../store/preference";
import PaginationFull from "../../../components/PaginationFull";
import useAuthStore from "../../../store/auth";
import TextInput from "../../../components/TextInput";
import {READONLY_MERCHANT} from "../../../utils/env";
import DateInput from "../../../components/DateInput";
import SimplePaymentList from "./components/SimplePaymentList";

function Payment() {
    const {t} = useTranslation();
    const {merchant, permissions} = useAuthStore();
    const isReadOnly = READONLY_MERCHANT || permissions?.includes('sub_user_read_only');
    const [sort, setSort] = useState({field: 'id', isAscending: false});
    const [page, setPage] = useState(1);
    const [showFilter, setShowFilter] = useState(false);
    const defaultFilter = {
        status: '',
        test: '',
        paymentUuid: '',
        supplier: '',
        source: '',
        channel: '',
        brokerReference: '',
        fromDate: '', // new Date(new Date().setMonth(new Date().getMonth() - 12)).toISOString().split('T')[0], // 12 months ago
        toDate: '', // Today
        paidFromDate: '', // Paid at from date
        paidToDate: '' // Paid at to date
    };
    const [filter, setFilter] = useState(defaultFilter)
    const [statusTabs, setStatusTabs] = useState(PAYMENT_LIST_STATUS_FOR_FILTER)
    const [statusCountEnabled, setStatusCountEnabled] = useState(false);
    const [linkToShow, setLinkToShow] = useState(null);
    const [showPaymentReportDialog, setShowPaymentReportDialog] = useState(false);
    const {paymentListStyle, togglePaymentListStyle} = usePreferenceStore();
    useEffect(() => setPage(1), [filter, sort]);

    useEffect(() => {
        const timer = setTimeout(() => setStatusCountEnabled(true), 1000);
        return () => clearTimeout(timer);
    }, []);

    useQuery(['payment_status_count', {
        fromDate: filter.fromDate,
        toDate: filter.toDate,
        paidFromDate: filter.paidFromDate,
        paidToDate: filter.paidToDate,
        test: filter.test,
        source: filter.source,
        brokerReference: filter.brokerReference,
        supplier: merchant?.allowSubVendors === true ? filter.supplier : '',
    }], async () => {
        const {data} = await apollo.query({
            query: GET_PAYMENT_STATUS_COUNT,
            variables: {
                filter: {
                    fromDate: filter.fromDate,
                    toDate: filter.toDate,
                    paidFromDate: filter.paidFromDate,
                    paidToDate: filter.paidToDate,
                    test: filter.test,
                    source: filter.source,
                    brokerReference: filter.brokerReference,
                    supplier: merchant?.allowSubVendors === true ? filter.supplier : '',
                }
            },
        });
        return data?.getPaymentStatusCount;
    }, {
        enabled: statusCountEnabled,
        keepPreviousData: true, onSettled: (data) => {
            if (data && Array.isArray(data)) {
                let updatedStatusTabs = [...statusTabs];
                data.forEach((item) => {
                    let foundIndex = updatedStatusTabs.findIndex(tab => tab.column === item.status);
                    if (foundIndex !== -1) {
                        updatedStatusTabs[foundIndex].count = parseInt(item.count);
                    }
                });
                setStatusTabs(updatedStatusTabs);
            }
        },
    });

    const fetchPaymentLinks = useQuery(['payment_links', {
        sort,
        page,
        filter,
        allowSubVendors: merchant?.allowSubVendors === true
    }], async () => {
        const {data} = await apollo.query({
            query: GET_PAYMENT_LIST, variables: {
                sortBy: sort.field?.toUpperCase(),
                sortDirection: sort.isAscending ? "ASC" : "DESC",
                page,
                first: 15,
                filter: {
                    status: filter.status,
                    test: filter.test,
                    paymentUuid: filter.paymentUuid,
                    supplier: merchant?.allowSubVendors === true ? filter.supplier : '',
                    source: filter.source,
                    channel: filter.channel,
                    brokerReference: filter.brokerReference,
                    fromDate: filter.fromDate,
                    toDate: filter.toDate,
                    paidFromDate: filter.paidFromDate,
                    paidToDate: filter.paidToDate,
                }
            },
        });
        return data?.getPaymentList || null;
    }, {keepPreviousData: false, refetchInterval: 30000},);

    const fetchMerchantSuppliersList = useQuery({
        queryKey: 'merchant_suppliers_list',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_MERCHANT_SUPPLIERS_LIST,
                variables: {},
            });
            return data?.getMerchantSuppliersList;
        },
        keepPreviousData: false,
        enabled: merchant?.allowSubVendors === true
    });

    const fetchPaymentChannels = useQuery({
        queryKey: 'payment_channels',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_PAYMENT_CHANNELS,
                variables: {},
            });
            return data?.getPaymentChannels || [];
        },
        keepPreviousData: false,
    });

    const fetchPaymentBrokerReferences = useQuery({
        queryKey: 'payment_broker_references',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_PAYMENT_BROKER_REFERENCES,
                variables: {},
            });
            return data?.getPaymentBrokerReferences || [];
        },
        keepPreviousData: false,
    });

    const records = fetchPaymentLinks?.data || null;
    const suppliersList = fetchMerchantSuppliersList?.isSuccess ? fetchMerchantSuppliersList?.data : null;
    const channelsList = fetchPaymentChannels?.isSuccess ? fetchPaymentChannels?.data : [];
    const brokerReferencesList = fetchPaymentBrokerReferences?.isSuccess ? fetchPaymentBrokerReferences?.data : [];

    const handleChangeSort = (field) => {
        if (sort.field === field) {
            setSort(prevState => ({...prevState, isAscending: !prevState.isAscending}));
        } else {
            setSort({field: field, isAscending: true});
        }
    };

    const clearFilters = () => {
        setFilter({
            ...defaultFilter,
            fromDate: '',
            toDate: '',
        });
    };

    return (<DashboardLayout>
        <PageHeader title={t('payment.page_title')} description={t('payment.page_description')}>
            <div className="flex items-center gap-3 flex-wrap">
                <IconButton onClick={() => setShowFilter(!showFilter)}>
                    <FunnelIcon className="w-5 h-5 text-cyan-500"/>
                </IconButton>
                <IconButton onClick={togglePaymentListStyle}>
                    <QueueListIcon className="w-5 h-5 text-blue-500"/>
                </IconButton>
                <div className="flex items-center gap-3 flex-wrap">
                    <ButtonInternalLink
                        hidden={isReadOnly}
                        secondary={true}
                        href="/merchant/payments/create-bulk">{t('payment.create_bulk_payments')}</ButtonInternalLink>
                    <Button
                        secondary={true}
                        hidden={permissions?.includes('sub_user_read_only')}
                        onClick={() => setShowPaymentReportDialog(true)}>{t('payment.download_reports')}</Button>
                    <ButtonInternalLink
                        hidden={isReadOnly}
                        href="/merchant/payments/create">{t('payment.create_payment_link')}</ButtonInternalLink>
                </div>
            </div>
        </PageHeader>
        <PaymentLinkDialog get={linkToShow} set={setLinkToShow}/>
        <PaymentReportGenerateDialog active={showPaymentReportDialog} toggle={setShowPaymentReportDialog}
                                     filter={filter} brokerReferencesList={brokerReferencesList}/>
        {showFilter &&
            <div
                className="my-4 shadow-sm bg-gray-50/50 border border-gray-100 rounded-2xl px-6 py-6 grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="col-span-3">
                    <TextInput name="status-filter" label={t('payment.filter.invoice_reference')} id="status-filter"
                               get={filter.paymentUuid}
                               set={v => setFilter({...filter, paymentUuid: v})}/>
                </div>
                <div className="col-span-2">
                    <DateInput
                        name="from-date-filter"
                        label={t('payment.filter.created_from_date') || 'Created From Date'}
                        id="from-date-filter"
                        get={filter.fromDate}
                        set={v => setFilter({...filter, fromDate: v})}
                    />
                </div>
                <div className="col-span-2">
                    <DateInput
                        name="to-date-filter"
                        label={t('payment.filter.created_to_date') || 'Created To Date'}
                        id="to-date-filter"
                        get={filter.toDate}
                        set={v => setFilter({...filter, toDate: v})}
                    />
                </div>
                <div className="col-span-2">
                    <DateInput
                        name="paid-from-date-filter"
                        label={t('payment.filter.paid_from_date') || 'Paid From Date'}
                        id="paid-from-date-filter"
                        get={filter.paidFromDate}
                        set={v => setFilter({...filter, paidFromDate: v})}
                    />
                </div>
                <div className="col-span-2">
                    <DateInput
                        name="paid-to-date-filter"
                        label={t('payment.filter.paid_to_date') || 'Paid To Date'}
                        id="paid-to-date-filter"
                        get={filter.paidToDate}
                        set={v => setFilter({...filter, paidToDate: v})}
                    />
                </div>
                <div className="col-span-2">
                    <SelectInput name="status-filter" label={t('payment.table.status')} id="status-filter"
                                 get={filter.status}
                                 set={v => setFilter({...filter, status: v})}>
                        {PAYMENT_LIST_STATUS_FOR_FILTER.map(s => <option key={s.column}
                                                                         value={s.column}>{s.name} {s.count ? `(${s.count})` : ''}</option>)}
                    </SelectInput>
                </div>
                <div className="col-span-2">
                    <SelectInput name="source-filter" label={t('payment.filter.create_from')} id="source-filter"
                                 get={filter.source}
                                 set={v => setFilter({...filter, source: v})}>
                        <option value="all">{t('payment.filter.all')}</option>
                        <option value="api">{t('payment.filter.api')}</option>
                        <option value="dashboard">{t('payment.filter.web_dashboard')}</option>
                    </SelectInput>
                </div>
                <div className="col-span-2">
                    <SelectInput name="test-filter" label={t('payment.filter.payment_environment')} id="test-filter"
                                 get={filter.test}
                                 set={v => setFilter({...filter, test: v})}>
                        <option value="">{t('payment.filter.all')}</option>
                        <option value="true">{t('payment.filter.test_mode')}</option>
                        <option value="false">{t('payment.filter.live_mode')}</option>
                    </SelectInput>
                </div>
                <div className="col-span-2">
                    <SelectInput name="broker-reference-filter" label={t('payment.filter.broker_reference') || 'Broker Reference'} id="broker-reference-filter"
                                 get={filter.brokerReference}
                                 set={v => setFilter({...filter, brokerReference: v})}>
                        <option value="">{t('payment.filter.all') || 'All'}</option>
                        {brokerReferencesList?.map(ref => <option key={ref} value={ref}>{ref}</option>)}
                    </SelectInput>
                </div>
                {merchant?.allowSubVendors === true && (
                    <div className="col-span-3">
                        <SelectInput name="supplier-filter" label={t('payment.filter.supplier')} id="supplier-filter"
                                     get={filter.supplier}
                                     set={v => setFilter({...filter, supplier: v})}>
                            <option value=""></option>
                            {suppliersList?.map(supplier => <option key={supplier.supplierUuid}
                                                                    value={supplier.supplierUuid}>{supplier.supplierName}</option>)}
                        </SelectInput>
                    </div>
                )}
                {channelsList?.length > 0 && (
                    <div className="col-span-2">
                        <SelectInput name="channel-filter" label={t('payment.filter.channel') || 'Channel'} id="channel-filter"
                                     get={filter.channel}
                                     set={v => setFilter({...filter, channel: v})}>
                            <option value="">{t('payment.filter.all') || 'All'}</option>
                            {channelsList.map(ch => <option key={ch} value={ch}>{ch}</option>)}
                        </SelectInput>
                    </div>
                )}
                <div className="col-span-2 flex items-end">
                    <Button
                        secondary={true}
                        onClick={clearFilters}
                        className=""
                    >
                        {t('payment.filter.clear_filters') || 'Clear Filters'}
                    </Button>
                </div>
            </div>}
        <Tabs tabs={statusTabs} get={filter?.status} set={v => setFilter({...filter, status: v})}/>
        {fetchPaymentLinks.isLoading && <Loading/>}
        {(fetchPaymentLinks.isSuccess && records) && <>
            {paymentListStyle === 'simple' && <SimplePaymentList handleChangeSort={handleChangeSort}
                                                                 records={records}
                                                                 sort={sort}
                                                                 setLinkToShow={setLinkToShow}
            />}
            {paymentListStyle === 'compact' && <CompactPaymentList handleChangeSort={handleChangeSort}
                                                                   records={records}
                                                                   sort={sort}
                                                                   setLinkToShow={setLinkToShow}
            />}
            <PaginationFull paginator={records?.paginatorInfo} get={page} set={setPage}/>

        </>}
    </DashboardLayout>);
}


export default Payment;
